import {inject, Injectable} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {registerLoggerWorker} from "@cm/browser-log"
import {CM_LOGGER_WEBASSEMBLY} from "@common/services/logging"
import {CorsAwareWorker, deviceMemory, isMobileDevice, TimeoutImplementation} from "@cm/browser-utils"

import {WebAssemblyWorkerManager} from "@cm/web-assembly"
import {WebWorker} from "@common/helpers/worker/web-worker"

const WORKER_ASSET_PATH = "assets/web-assembly-worker.js"

export function spawnNewWebWorker(name: string, logger?: CmLogger): WebWorker {
    const scriptOrigin = new URL(import.meta.url).origin
    const isSameOrigin = scriptOrigin === location.origin
    const workerUrl = `${scriptOrigin}/${WORKER_ASSET_PATH}`

    logger?.debug("Spawning web worker", {scriptOrigin, pageOrigin: location.origin, isSameOrigin, workerUrl})

    const corsAwareWorker = new CorsAwareWorker(workerUrl, {type: "module", name})
    corsAwareWorker.addEventListener("message", ({data}) => logger?.debug(`${name} got message`, data))
    corsAwareWorker.addEventListener("messageerror", ({data}) => logger?.warn(`${name} got message error`, {data}))
    corsAwareWorker.addEventListener("error", (error) => logger?.error(`${name} got error`, error))

    const webWorker = new WebWorker(corsAwareWorker)
    const unregister = registerLoggerWorker(webWorker)
    webWorker.addOnTerminate(unregister)
    return webWorker
}

@Injectable({
    providedIn: "root",
})
export class WebAssemblyWorkerService extends WebAssemblyWorkerManager<ReturnType<typeof setTimeout>> {
    constructor() {
        const logger = inject(CM_LOGGER_WEBASSEMBLY)
        logger.debug("Creating web assembly worker service")
        super((name) => spawnNewWebWorker(name, logger), TimeoutImplementation, logger, isMobileDevice, deviceMemory)
    }
}
